import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material'
import { type FC, useState } from 'react'
import { FeedbackPill } from './FeedbackPill'
import { VerdictPill } from './VerdictPill'

interface TimelineMessage {
    id: string
    role: string
    text: string
    action?: string | null
    metadata?: unknown
    judgeVerdict?: string | null
    judgeReason?: string | null
    feedback?: string | null
    durationMs?: number | null
    createdAt: string | Date
}

interface MessageTimelineProps {
    messages: TimelineMessage[]
}

export const MessageTimeline: FC<MessageTimelineProps> = ({ messages }) => {
    return (
        <Stack spacing={1.5}>
            {messages.map((m) => (
                <MessageItem key={m.id} message={m} />
            ))}
        </Stack>
    )
}

interface MessageItemProps {
    message: TimelineMessage
}

const MessageItem: FC<MessageItemProps> = ({ message }) => {
    const isBot = message.role === 'bot'
    const [reasonOpen, setReasonOpen] = useState(false)
    const [metadataOpen, setMetadataOpen] = useState(false)

    const hasMetadata =
        message.metadata != null &&
        typeof message.metadata === 'object' &&
        !Array.isArray(message.metadata) &&
        Object.keys(message.metadata as Record<string, unknown>).length > 0
    const hasReason = !!message.judgeReason
    const timestamp = new Date(message.createdAt)

    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                bgcolor: isBot ? 'background.paper' : 'background.default',
            }}
            data-testid={`bot-audit-thread-message-${message.id}`}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.75 }}
            >
                <Box
                    sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        color: isBot ? 'primary.main' : 'text.secondary',
                        minWidth: 32,
                    }}
                >
                    {isBot ? 'Bot' : 'User'}
                </Box>
                {message.action ? (
                    <Box
                        sx={{
                            fontSize: '0.7rem',
                            color: 'text.secondary',
                            textTransform: 'lowercase',
                        }}
                    >
                        {message.action}
                    </Box>
                ) : null}
                {isBot ? <VerdictPill verdict={message.judgeVerdict} /> : null}
                {isBot ? <FeedbackPill feedback={message.feedback} /> : null}
                <Box sx={{ flex: 1 }} />
                {isBot && message.durationMs != null ? (
                    <Box sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {message.durationMs}ms
                    </Box>
                ) : null}
                <Box
                    sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                    title={timestamp.toLocaleString()}
                >
                    {timestamp.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                    })}
                </Box>
            </Stack>

            <Typography
                variant="body2"
                sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.85rem',
                    color: 'text.primary',
                }}
            >
                {message.text}
            </Typography>

            {hasReason ? (
                <Box sx={{ mt: 1 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ color: 'text.secondary' }}
                    >
                        <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            onClick={() => setReasonOpen((o) => !o)}
                            aria-expanded={reasonOpen}
                            aria-label={
                                reasonOpen
                                    ? 'Collapse judge reason'
                                    : 'Expand judge reason'
                            }
                        >
                            {reasonOpen ? (
                                <ExpandLess fontSize="small" />
                            ) : (
                                <ExpandMore fontSize="small" />
                            )}
                        </IconButton>
                        <Box
                            sx={{
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                fontWeight: 500,
                            }}
                        >
                            Judge reason
                        </Box>
                    </Stack>
                    <Collapse in={reasonOpen}>
                        <Box
                            sx={{
                                pl: 3,
                                pt: 0.5,
                                fontSize: '0.8rem',
                                color: 'text.secondary',
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {message.judgeReason}
                        </Box>
                    </Collapse>
                </Box>
            ) : null}

            {hasMetadata ? (
                <Box sx={{ mt: 1 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ color: 'text.secondary' }}
                    >
                        <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            onClick={() => setMetadataOpen((o) => !o)}
                            aria-expanded={metadataOpen}
                            aria-label={
                                metadataOpen
                                    ? 'Collapse metadata'
                                    : 'Expand metadata'
                            }
                        >
                            {metadataOpen ? (
                                <ExpandLess fontSize="small" />
                            ) : (
                                <ExpandMore fontSize="small" />
                            )}
                        </IconButton>
                        <Box
                            sx={{
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                fontWeight: 500,
                            }}
                        >
                            Metadata
                        </Box>
                    </Stack>
                    <Collapse in={metadataOpen}>
                        <Box
                            component="pre"
                            sx={{
                                pl: 3,
                                pt: 0.5,
                                fontSize: '0.75rem',
                                fontFamily: 'monospace',
                                color: 'text.secondary',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                m: 0,
                            }}
                        >
                            {JSON.stringify(message.metadata, null, 2)}
                        </Box>
                    </Collapse>
                </Box>
            ) : null}
        </Box>
    )
}
